import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import { localStorageSync } from 'ngrx-store-localstorage';
import {
  provideCacheableAnimationLoader,
  provideLottieOptions,
} from 'ngx-lottie';
import player from 'lottie-web';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpLoaderFactory } from './shared/utils/loader-factory';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { MetaReducer, provideStore } from '@ngrx/store';
import { DefaultDataServiceConfig, provideEntityData } from '@ngrx/data';
import { entityConfig } from './entity-metadata';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  AuthModule,
  ErrorInterceptor,
  TokenInterceptor,
} from '@kimbocare/auth';
import {
  AppUpdateService,
  reducer,
  StorageService,
  SyncStorageService,
} from '@kimbocare/core';
import { TenantInterceptor } from './core/interceptors/tenant.interceptor';

export function localStorageSyncReducer(reducer: any): any {
  return localStorageSync({
    keys: ['auth'], // Specify the state slices to persist
    rehydrate: true, // Automatically rehydrate the state from localStorage
  })(reducer);
}

const defaultDataServiceConfig: DefaultDataServiceConfig = {
  root: `${environment.kimboPath}/${environment.apiProvider}`,
  timeout: 3000, // request timeout
};

export const metaReducers: MetaReducer<any>[] = [localStorageSyncReducer];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withHashLocation()),
    provideAnimations(),
    provideClientHydration(),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withJsonpSupport(),
    ),
    provideToastr(),
    importProvidersFrom(AuthModule.forRoot()),
    provideStore(
      { appState: reducer },
      {
        metaReducers: metaReducers,
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: false,
        },
      },
    ),
    provideEntityData(entityConfig),
    importProvidersFrom(
      StoreDevtoolsModule.instrument({ logOnly: !isDevMode() }),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    provideLottieOptions({ player: () => player }),
    provideCacheableAnimationLoader(),
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: 'env', useValue: environment },
    {
      provide: DefaultDataServiceConfig,
      useValue: defaultDataServiceConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),

    StorageService,
    provideAppInitializer(() => {
      const initializerFn = (
        (ds: SyncStorageService) => () =>
          ds.syncAll()
      )(inject(SyncStorageService));
      return initializerFn();
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AppUpdateService,
    provideAppInitializer(() => {
      const initializerFn = (
        (ds: AppUpdateService) => () =>
          ds.init()
      )(inject(AppUpdateService));
      return initializerFn();
    }),
  ],
};
